import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, MapPin, Sparkles } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../lib/api";

/**
 * /vote — single-screen, mobile-first, share-friendly poll.
 *
 *   • 9:16 vertical hero so a phone screenshot becomes an instant reel.
 *   • One tap on any region → vote registers anonymously AND routes to
 *     /browse?region=<X> so the user immediately sees matching campsites.
 *   • Live percentage bars visible BEFORE voting (everyone can see counts).
 *   • A QR code top-right links back to /vote itself, so if you screenshot
 *     this page and post it as a static reel, viewers can scan to play.
 *
 * Designed to be its own dedicated route (not just a section on Landing)
 * so it's clean to share: a single, focused, no-distractions vote screen.
 */

const POLL_ID = "today";  // auto-rotates daily via /api/polls/today

const FALLBACK_GRADS = [
  { grad: "linear-gradient(135deg,#0ea5e9 0%,#3b82f6 100%)", accent: "#fde047" },
  { grad: "linear-gradient(135deg,#fb923c 0%,#facc15 100%)", accent: "#ffffff" },
  { grad: "linear-gradient(135deg,#7c3aed 0%,#ec4899 100%)", accent: "#fde047" },
  { grad: "linear-gradient(135deg,#16a34a 0%,#0d9488 100%)", accent: "#fde047" },
  { grad: "linear-gradient(135deg,#dc2626 0%,#7c2d12 100%)", accent: "#fde047" },
  { grad: "linear-gradient(135deg,#0891b2 0%,#16a34a 100%)", accent: "#ffffff" },
];
const REGION_STYLES = {
  lake_district: FALLBACK_GRADS[0], cornwall: FALLBACK_GRADS[1],
  scotland: FALLBACK_GRADS[2], wales: FALLBACK_GRADS[3],
  peak_district: FALLBACK_GRADS[4], yorkshire: FALLBACK_GRADS[5],
};

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");

export default function Vote() {
  const [poll, setPoll] = useState(null);
  const [voting, setVoting] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/polls/${POLL_ID}`);
      setPoll(data);
    } catch {
      // soft-fail — page still renders with neutral state
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vote = async (opt) => {
    if (voting) return;
    setVoting(opt.key);
    try {
      const { data } = await api.post(`/polls/${poll.poll_id}/vote`, { option: opt.key });
      setPoll(data);
      window.dispatchEvent(new CustomEvent("cc:presence-bump", { detail: { source: "vote-page" } }));
      setTimeout(() => {
        const param = poll.filter_param || "region";
        navigate(`/browse?${encodeURIComponent(param)}=${encodeURIComponent(opt.browse_filter)}`);
      }, 700);
    } catch {
      setVoting(null);
    }
  };

  if (!poll) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0e0e0e] text-white">
        <Loader2 className="w-6 h-6 animate-spin" /> <span className="ml-3 mono text-sm">Loading vote…</span>
      </div>
    );
  }

  const max = Math.max(1, ...poll.options.map((o) => o.count));

  return (
    <div className="min-h-screen bg-[#0e0e0e] text-white" data-testid="vote-page">
      {/* HERO — a deliberately 9:16-aspect block so a phone screenshot of just
          this section becomes a postable reel. */}
      <section
        className="relative overflow-hidden mx-auto"
        style={{
          background: "linear-gradient(160deg,#1e3a8a 0%,#7c3aed 35%,#ec4899 70%,#f97316 100%)",
          aspectRatio: "9 / 16",
          maxWidth: 540,
        }}
        data-testid="vote-hero"
      >
        {/* Grain dots + soft orb for depth */}
        <div
          className="absolute inset-0 opacity-25 pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "14px 14px",
          }}
        />
        <div
          className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: "#fde047" }}
        />

        <div className="relative z-10 h-full flex flex-col justify-between p-5 sm:p-6">
          {/* Top row — eyebrow + QR for screenshotted reel */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/40 backdrop-blur-sm">
                <Sparkles className="w-3 h-3 text-[#fde047]" />
                <span className="text-[10px] font-bold uppercase tracking-[0.25em]">Vote · live</span>
              </div>
              <div className="mt-3 text-[10px] mono uppercase tracking-[0.2em] text-white/80 inline-flex items-center gap-1.5">
                <MapPin className="w-3 h-3" /> {poll.total.toLocaleString("en-GB")} campers voted
              </div>
            </div>
            <div className="bg-white p-1.5 shrink-0" data-testid="vote-hero-qr">
              <QRCodeSVG value={`${PUBLIC_URL}/vote`} size={64} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
            </div>
          </div>

          {/* Question — dynamic per day */}
          <div className="my-3">
            <h1 className="font-display text-[40px] sm:text-[56px] leading-[0.95] drop-shadow-[0_4px_18px_rgba(0,0,0,0.55)] uppercase">
              {poll.question}
            </h1>
            <p className="text-base sm:text-lg text-white/90 mt-2 font-semibold">
              Tap once. See the campsites.
            </p>
          </div>

          {/* Buttons — full-bleed, big, instant feedback */}
          <div className="space-y-2">
            {poll.options.map((opt, idx) => {
              const style = REGION_STYLES[opt.key] || FALLBACK_GRADS[idx % FALLBACK_GRADS.length];
              const isMine = poll.my_choice === opt.key;
              const isVoting = voting === opt.key;
              const widthPct = max > 0 ? (opt.count / max) * 100 : 0;
              return (
                <button
                  key={opt.key}
                  onClick={() => vote(opt)}
                  disabled={!!voting}
                  className={`relative overflow-hidden block w-full text-left transition-transform active:scale-[0.98] disabled:cursor-wait border-2 ${isMine ? "border-[#fde047] shadow-[3px_3px_0_0_#fde047]" : "border-transparent"}`}
                  style={{ background: style.grad }}
                  data-testid={`vote-btn-${opt.key}`}
                >
                  {/* Live fill bar */}
                  <div
                    className="absolute inset-y-0 left-0 transition-[width] duration-700 ease-out pointer-events-none"
                    style={{ width: `${widthPct}%`, background: "rgba(255,255,255,0.22)", mixBlendMode: "overlay" }}
                  />
                  <div className="relative p-3 sm:p-3.5 flex items-center justify-between gap-3 text-white">
                    <div className="font-display text-lg sm:text-xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]">
                      {opt.label}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="mono text-[11px] tracking-wider opacity-90 text-right">
                        <div className="font-display text-base" style={{ color: style.accent }}>
                          {opt.pct}%
                        </div>
                        <div className="text-[10px]">{opt.count.toLocaleString("en-GB")}</div>
                      </div>
                      {isVoting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <ArrowRight className="w-5 h-5 opacity-90" />
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 border-t border-white/25 pt-3 mt-3">
            <div className="font-display text-base">camp-compass.uk</div>
            <div className="text-[9.5px] mono uppercase tracking-[0.25em] text-white/80">
              one tap · vote + browse
            </div>
          </div>
        </div>
      </section>

      {/* Below-the-fold guidance for direct visitors who landed here from the link */}
      <section className="max-w-xl mx-auto px-5 py-10 text-white space-y-4">
        <h2 className="font-display text-2xl">Why we built this</h2>
        <p className="text-sm text-[#bbb] leading-relaxed">
          Most camping sites bury 22,000 UK campsites behind 7 dropdowns. We bury
          ours behind one tap. Pick your region above — we'll show you matching
          sites instantly, no signup needed.
        </p>
        <p className="text-sm text-[#bbb] leading-relaxed">
          Already voted? <Link to="/browse" className="text-[#fde047] underline">Browse everything</Link> ·{" "}
          <Link to="/" className="text-[#fde047] underline">Back to home</Link>.
        </p>
        <p className="text-[11px] mono text-[#666] pt-4">
          Anonymous voting. One vote per browser. Tap a different option any time to change your mind.
        </p>
      </section>
    </div>
  );
}
